import {
    getTokenVariable,
    useUserInfo,
    useBusinessStore,
    useRegisterConfiguration,
} from '@register-ui/utils';
import { CustomerMetricsContext } from '@moniebook-web/utils';
import {
    identifyUser,
    setup,
    getSDK,
    pushData,
    type CxMetricOptions,
} from '@web-platform/cx-metrics-browser';
import { useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { CustomerMetricsOfflineSync } from './sync';

export interface CustomerMetricsProviderProps {
    cxMetricsOptions: CxMetricOptions;
}

export function CustomerMetricsProvider({
    cxMetricsOptions,
    children,
}: Readonly<PropsWithChildren<CustomerMetricsProviderProps>>) {
    const [isReady, setIsReady] = useState(false);
    const token = getTokenVariable();
    const staff = useUserInfo();
    const { businessInfo } = useBusinessStore();
    const { config } = useRegisterConfiguration();

    // Setup CX Metrics
    useEffect(() => {
        if (isReady) return;

        setup(cxMetricsOptions).then(() => setIsReady(true));
    }, [cxMetricsOptions, isReady]);

    // Identify user
    useEffect(() => {
        if (!staff || !isReady || !businessInfo) return;

        identifyUser({
            customerIdentifier: staff.id,
            user: {
                authToken: token,
                id: staff.authUserId,
                first_name: staff.firstName,
                last_name: staff.lastName,
                businessId: businessInfo.businessId,
                email: staff.email,
                business_name: businessInfo.businessName,
                branchName: businessInfo.branchName,
                registerName: businessInfo.registerName,
                branchId: config.branchId,
                registerId: config.registerId,
            },
        });
    }, [staff, token, isReady, businessInfo, config]);

    const value = useMemo(
        () => ({
            isReady,
            getSDK: isReady ? getSDK : undefined,
            pushData: isReady ? pushData : undefined,
            platform: 'WEB_REGISTER',
        }),
        [isReady],
    );

    return (
        <CustomerMetricsContext.Provider value={value}>
            <CustomerMetricsOfflineSync />
            {children}
        </CustomerMetricsContext.Provider>
    );
}
